import { useReactFlow, NodeProps } from '@xyflow/react'
import { Steps } from 'intro.js-react'
import {
  RefreshCw,
  UserPlus,
  HelpCircle,
  Copy,
  Expand,
  Minimize,
  ArrowUp,
  ArrowDown,
  EyeOff,
  Eye,
  Shield,
  BookOpen,
  FileText,
  ChevronUp,
  ChevronDown,
  GripVertical,
  Printer,
  Download,
  Trash,
  RefreshCcw,
  FilePlus,
  Check,
  X,
  Settings,
  Files,
  FileOutput,
  Image,
  LayoutTemplate,
  Plus,
  Clipboard,
} from 'lucide-react'
import React, { memo, useRef, useState, useEffect, useCallback, useMemo } from 'react'

import { useParams } from 'react-router'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'
import NodeExports from '../export-to/NodeExportTo'
import NodeTags from '../tags/NodeTags'
import '~/styles/introjs.css'
import styles from '@/styles/NodeComposite.module.css'

import DocumentStyleSelector, { DocumentStyle } from '../../document//DocumentStyleSelector'
import MarkdownRenderer from '@/components/markdown-renderer/MarkdownRenderer'
import PDFExportDialog from '@/components/dialogs/PDFExportDialog'
import SectionContextMenu from '@/components/menus/SectionContextMenu'
import SectionEditor from '@/components/sections/SectionEditor'
import DocumentPDFRenderer from '@/components/pdf/DocumentPDFRenderer'
import NodeReferenceSelectorDialog from '@/components/dialogs/reference-selector-dialog/NodeReferenceSelectorDialog'
import FileResourcingDialog from '@/components/dialogs/FileResourcingDialog'
import NodeConfigurationDialog, { NodeConfiguration } from '@/components/dialogs/configuration-dialog/NodeConfigurationDialog'
import NodeDataSettingsDialog from '@/components/dialogs/data-settings-dialog/NodeDataSettingsDialog'
import UpdateWithAIDialog from '@/components/dialogs/UpdateWithAIDialog'

import useDocumentState from '@/hooks/useDocumentState'
import useSectionManagement from '@/hooks/useSectionManagement'
import useDataSettings from '@/hooks/useDataSettings'
import useIntroTour from '@/hooks/useIntroTour'
import { useFileStore } from '@/stores/fileStore'
import { useUser } from '@/context/UserContext'

import {
  copyToClipboard,
  extractSectionsFromMarkdown,
} from '@/utils/documentUtils'
import { PDFViewer, pdf, BlobProvider } from '@react-pdf/renderer'

// Update the handleScroll function to better handle middle mouse button
const handleScroll = (element, deltaY, buttons) => {
  if (!element) return;

  const { scrollTop, scrollHeight, clientHeight } = element;
  const isAtTop = scrollTop <= 0;
  const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2;
  const isScrollingUp = deltaY < 0;
  const isScrollingDown = deltaY > 0;

  // Handle middle mouse button scrolling (button code 4) or regular wheel scrolling
  const isMiddleMouseScrolling = buttons === 4;

  if ((isScrollingUp && !isAtTop) || (isScrollingDown && !isAtBottom) || isMiddleMouseScrolling) {
    element.scrollTop += deltaY;
    return true; // Event was handled
  }
  return false; // Let it bubble for panning
};

const SECTION_ITEM_TYPE = 'section'

// Interface for the drag item
interface SectionDragItem {
  id: string
  index: number
}

interface CompositeNodeData {
  contentMarkdown?: string
  documentDescription?: string
  statusMessage?: string
  isMarkdownExpanded?: boolean
  assignedEmails?: string[]
  configuration?: CompositeNodeConfiguration
  label?: string
  selectedSection?: Section | null
  sectionStates?: Record<string, string>
  disabledSections?: Record<string, boolean>
  protectedSections?: Record<string, boolean>
  documentStyle?: any
  context?: string
  linkedKnowledge?: string
  dataSettings?: NodeDataSettings
  headerTemplate?: string
  footerTemplate?: string
  styleSettings?: {
    fontFamily?: string
    fontSize?: string
    lineSpacing?: string
    marginTop?: string
    marginBottom?: string
    marginLeft?: string
    marginRight?: string
    pageSize?: string
    orientation?: string
  }
  publishedFiles?: PublishedFile[]
  sectionCommand?: {
    command: 'Draft' | 'Detailed' | 'Styled'
    sectionId: string
    sectionTitle: string
    content: string
    'sysagent-cmd'?: string
    'sysagent-response'?: string
    for_review?: string
  }
  isMinimized?: boolean
  isLocked?: boolean
  inputdata?: Record<string, Record<string, { value: string; enabled: boolean }>>
  documentCommand?: {
    'sysagent-cmd'?: string
    'sysagent-response'?: string
    mode?: string
    processing?: boolean
    error?: string
    processedAt?: string
    message?: string
  }
  sectionOrder?: string[] // Array of section IDs in order
  resources?: Resource[] // Array of resource files (images, PDFs, etc.)
  sectionMeta?: Record<string, { updatedAt?: number; updatedBy?: string }>
}

interface CompositeNodeConfiguration extends NodeConfiguration {
  markdownFormatVisible: boolean
  showPublishedFiles: boolean
  enableDragAndDrop: boolean
  autoRefreshSections: boolean
}

interface Section {
  id: string
  title: string
  content: string
}

interface NodeDataSettings {
  inputLinks: any[]
  outputLinks: any[]
  availableNodes: any[]
}

interface PublishedFile {
  id: string
  filename: string
  dateCreated: string
  url?: string
  sections: string[] // IDs of included sections
  fileId?: string // ID in file system
}

interface Resource {
  id: string
  name: string
  type: string
  url?: string
  fileId?: string
  content?: string
}

const NodeComposite: React.FC<NodeProps & { data: CompositeNodeData }> = (
  props
) => {
  const { id, data, selected } = props
  const { updateNodeData } = useReactFlow()
  const { projectId } = useParams()
  const user = useUser()
  const nodeBaseContainerRef = useRef<HTMLDivElement | null>(null)
  const markdownPreviewRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  
  // Context menu for sections
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    sectionId: string;
    sectionName: string;
  }>( {
    visible: false,
    x: 0,
    y: 0,
    sectionId: '',
    sectionName: '',
  })

  // State for file dialog
  const [fileDialogVisible, setFileDialogVisible] = useState(false)
  // State for publishing dialog
  const [publishDialogVisible, setPublishDialogVisible] = useState(false)
  const [newPublishName, setNewPublishName] = useState('')
  const [selectedSectionsForPublish, setSelectedSectionsForPublish] = useState<string[]>([])
  
  // State for section reordering
  const [isDragging, setIsDragging] = useState(false)
  
  // State for header/footer editor
  const [headerFooterDialogVisible, setHeaderFooterDialogVisible] = useState(false)
  const [editingHeaderFooter, setEditingHeaderFooter] = useState<'header' | 'footer' | null>(null)

  // State for reference dialog
  const [isReferenceDialogVisible, setIsReferenceDialogVisible] = useState(false)
  
  // Add missing minimizeLevel state
  const [minimizeLevel, setMinimizeLevel] = useState<'normal' | 'medium' | 'minimum'>(
    (typeof data.minimizeLevel === 'string' &&
      (data.minimizeLevel === 'normal' || data.minimizeLevel === 'medium' || data.minimizeLevel === 'minimum'))
      ? data.minimizeLevel
      : 'normal'
  )
  
  const {
    files,
    fileGroups,
    fetchFiles,
    uploadFile,
    deleteFile,
    downloadFile,
  } = useFileStore()

const {
    contentMarkdown,
    setContentMarkdown,
    statusMessage,
    setStatusMessage,
    assignedEmails,
    setAssignedEmails,
    isLocked,
    setIsLocked,
    isMinimized,
    setIsMinimized,
    isMarkdownExpanded,
    setIsMarkdownExpanded,
    nodeConfig,
    setNodeConfig,
    documentStyle,
    setDocumentStyle,
    context,
    setContext,
    linkedKnowledge,
    setLinkedKnowledge,
    isConfigDialogVisible,
    setIsConfigDialogVisible,
    isUpdateWithAIDialogVisible,
    setIsUpdateWithAIDialogVisible,
    isDataSettingsDialogVisible,
    setIsDataSettingsDialogVisible,
    showDebugInfo,
    setShowDebugInfo,
    copyFeedback,
    setCopyFeedback,
    syncNodeData,
    handleLock,
    handleMinimize,
    handleUpdateWithAI,
    handleStyleChange,
    handleContentChange,
    handleContextChange,
    handleLinkedKnowledgeChange,      } = useDocumentState(id, data, updateNodeData)

  useEffect(() => {
    setContentMarkdown(data.contentMarkdown || '')
  }, [data.contentMarkdown])

  // Status message (local only)
  // No need to sync with shared state

  // Assigned emails
  useEffect(() => {
    setAssignedEmails(data.assignedEmails || [])
  }, [data.assignedEmails])

  // Lock/minimize state
  useEffect(() => {
    setIsLocked(data.isLocked || false)
  }, [data.isLocked])

  useEffect(() => {
    setIsMinimized(data.isMinimized || false)
  }, [data.isMinimized])

  // Markdown expanded
  useEffect(() => {
    setIsMarkdownExpanded(data.isMarkdownExpanded || false)
  }, [data.isMarkdownExpanded])

  // Node configuration
  useEffect(() => {
    setNodeConfig((data.configuration!) || nodeConfig)
    // eslint-disable-next-line
  }, [data.configuration])

  // Document style
  useEffect(() => {
    setDocumentStyle(data.documentStyle || {})
  }, [data.documentStyle])

  // Context
  useEffect(() => {
    setContext(data.context || '')
  }, [data.context])

  // Linked knowledge
  useEffect(() => {
    setLinkedKnowledge(data.linkedKnowledge || '')
  }, [data.linkedKnowledge])
  
  // Initialize section states and orders
  const {
    sections,
    selectedSectionId,
    setSelectedSectionId,
    editingSectionContent,
    setEditingSectionContent,
    isEditingSection,
    setIsEditingSection,
    sectionStates,
    setSectionStates,
    sectionReviews,
    setSectionReviews,
    handleSectionSelect,
    handleSectionEdit,
    handleSectionEditSave,
    handleStartEditing,
    handleCancelEditing,
    handleSectionStateChange,
    handleAcceptReview,
    handleRejectReview,
  } = useSectionManagement(id, data, syncNodeData, contentMarkdown, setContentMarkdown)

  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleRemoveInputLink,
    handleSyncInputLink,
    handleOpenDataSettingsDialog,
    generateLinkedKnowledgeText,
    prepareSectionsForDataSettings,
    prepareExposedDataFields,
    fetchAvailableDocumentNodes,
  } = useDataSettings(
    id,
    data,
    syncNodeData,
    showDebugInfo,
    context,
    sections,
    contentMarkdown,
    setIsDataSettingsDialogVisible
  )

  const markdownTextareaRef = useRef<HTMLTextAreaElement | null>(null); // Renamed for clarity

  const { introEnabled, setIntroEnabled, introSteps, introOptions } = useIntroTour(id, selectedSectionId)

  // Initialize minimize level from data
  useEffect(() => {
    if (data.minimizeLevel === undefined) {
      updateNodeData(id, { ...data, minimizeLevel: 'normal' });
    } else {
      setMinimizeLevel(data.minimizeLevel);
      setIsMinimized(data.minimizeLevel !== 'normal');
    }
  }, [data.minimizeLevel, id, data, updateNodeData]);

  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {};
    
    if (contentMarkdown && contentMarkdown.trim()) {
      const wordCount = contentMarkdown.trim().split(/\s+/).length;
      minifiedInfo.content = `${wordCount} words`;
    }
    
    if (sections && sections.size > 0) {
      minifiedInfo.sections = `${sections.size} section${sections.size !== 1 ? 's' : ''}`;
    }
    
    if (data.publishedFiles && data.publishedFiles.length > 0) {
      minifiedInfo.published = `${data.publishedFiles.length} file${data.publishedFiles.length !== 1 ? 's' : ''}`;
    }
    
    if (data.resources && data.resources.length > 0) {
      minifiedInfo.resources = `${data.resources.length} resource${data.resources.length !== 1 ? 's' : ''}`;
    }
    
    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {};
    const hasChanges = JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo);
    
    if (hasChanges) {
      updateNodeData(id, { ...data, minifiedInfo });
    }
  }, [contentMarkdown, sections, data.publishedFiles, data.resources, data.minifiedInfo, id, data, updateNodeData]);

  // Only call syncNodeData in response to user actions, not in useEffect

  // Example: handle markdown expand
  const toggleMarkdownExpand = () => {
    setIsMarkdownExpanded(!isMarkdownExpanded)
    syncNodeData({ isMarkdownExpanded: !isMarkdownExpanded })
  }

  // --- Remove any syncNodeData calls from useEffect that listen to shared state ---
  // For initialization, only set local state or call syncNodeData if not present (but not in response to shared state changes)
  useEffect(() => {
    // Only set defaults if fields are missing (not on every update)
    if (!data.disabledSections) {
      syncNodeData({ disabledSections: {} })
    }
    if (!data.protectedSections) {
      syncNodeData({ protectedSections: {} })
    }
    if (!data.publishedFiles) {
      syncNodeData({ publishedFiles: [] })
    }
    if (!data.headerTemplate) {
      syncNodeData({ headerTemplate: '# [Document Title]\n\n**Date:** [Current Date]' })
    }
    if (!data.footerTemplate) {
      syncNodeData({ footerTemplate: 'Page [Page Number] of [Total Pages]\n\n© [Current Year] - Confidential' })
    }
    if (!data.styleSettings) {
      syncNodeData({ 
        styleSettings: {
          fontFamily: 'Times New Roman',
          fontSize: '12pt',
          lineSpacing: '1.5',
          marginTop: '1in',
          marginBottom: '1in',
          marginLeft: '1in',
          marginRight: '1in',
          pageSize: 'A4',
          orientation: 'portrait'
        }
      })
    }
    if (!data.resources) {
      syncNodeData({ resources: [] })
    }
    if (!data.sectionMeta) {
      syncNodeData({ sectionMeta: {} })
    }
    // Section order initialization
    if (sections.size > 0 && (!data.sectionOrder || data.sectionOrder.length === 0 || 
        data.sectionOrder.length !== sections.size)) {
      const newOrder = Array.from(sections.keys())
      syncNodeData({ sectionOrder: newOrder })
    }

  // eslint-disable-next-line
  }, []) // Only run once on mount

  // Get sorted sections based on sectionOrder
  const getSortedSections = useCallback(() => {
    const sectionOrder = data.sectionOrder || []
    const sectionsArray: [string, Section][] = Array.from(sections.entries())
    
    // If we have an order, sort sections based on that
    if (sectionOrder.length > 0) {
      return sectionsArray.sort((a, b) => {
        const indexA = sectionOrder.indexOf(a[0])
        const indexB = sectionOrder.indexOf(b[0])
        // If a section isn't in the order, put it at the end
        if (indexA === -1) return 1
        if (indexB === -1) return -1
        return indexA - indexB
      })
    }
    
    // Otherwise return sections in their natural order
    return sectionsArray
  }, [sections, data.sectionOrder])

  // Handle section reordering
  const handleMoveSection = useCallback((sectionId: string, direction: 'up' | 'down') => {
    // Get current section order or initialize if empty
    let sectionOrder = [...(data.sectionOrder || [])]
    
    // If sectionOrder is empty but we have sections, create initial order
    if (sectionOrder.length === 0 && sections.size > 0) {
      sectionOrder = Array.from(sections.keys())
    }
    
    const currentIndex = sectionOrder.indexOf(sectionId)
    
    // Debug section order
    console.log("Current section order:", sectionOrder, "Moving section:", sectionId, "Current index:", currentIndex)
    
    if (currentIndex === -1) {
      console.warn("Section not found in order:", sectionId)
      return // Section not found in order
    }
    
    let newIndex: number
    if (direction === 'up' && currentIndex > 0) {
      newIndex = currentIndex - 1
    } else if (direction === 'down' && currentIndex < sectionOrder.length - 1) {
      newIndex = currentIndex + 1
    } else {
      return // Can't move further in this direction
    }
    
    // Swap positions
    [sectionOrder[currentIndex], sectionOrder[newIndex]] = [sectionOrder[newIndex], sectionOrder[currentIndex]]
    
    // Debug new order
    console.log("Updated section order:", sectionOrder)
    
    // Update section order
    syncNodeData({ sectionOrder })
  }, [data.sectionOrder, sections, syncNodeData])

  // Handle toggling section disabled state
  const handleToggleSectionDisabled = useCallback((sectionId: string) => {
    const disabledSections = { ...(data.disabledSections || {}) }
    disabledSections[sectionId] = !disabledSections[sectionId]
    syncNodeData({ disabledSections })
  }, [data.disabledSections, syncNodeData])

  // Handle toggling section protected state
  const handleToggleSectionProtected = useCallback((sectionId: string, protectedStatus: boolean) => {
    const protectedSections = { ...(data.protectedSections || {}) }
    protectedSections[sectionId] = protectedStatus
    syncNodeData({ protectedSections })
  }, [data.protectedSections, syncNodeData])

  // Handle section context menu
  const handleSectionContextMenu = useCallback((event: React.MouseEvent, sectionId: string, sectionName: string) => {
    event.preventDefault()
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      sectionId,
      sectionName
    })
  }, [])

  // Handle refreshing a section
  const handleRefreshSection = useCallback((sectionId: string) => {
    console.log("trying to refresh section: ", sectionId)
    if (!sections.has(sectionId)) return
    
    const section = sections.get(sectionId)
    console.log("found section: ", section)

    if (!section) return
    
    console.log(`Refreshing section "${section.title}"...`)
    
    // Use the sectionTitle to identify the source in references
    const sectionTitle = section.title
    
    // Check if this section is protected
    const isProtected = data.protectedSections?.[sectionId] || false
    if (isProtected) {
      console.log(`Section "${sectionTitle}" is protected and cannot be refreshed.`)
      setTimeout(() => setStatusMessage(''), 3000)
      return
    }
    
    // Get all nodes in the flow
    const allNodes = (window as any).__flowGetAllNodes?.() || []
    
    // Try to find the source of this section in setReferences
    let sourceFound = false
    let newContent = null
    
    if (data.setReferences) {
      for (const [nodeId, dataPointIds] of Object.entries(data.setReferences)) {
        // Find the source node
        const sourceNode = allNodes.find((n: any) => n.id === nodeId)
        if (!sourceNode) continue
       
        // Check each data point to see if it matches our section
        for (const dpId of dataPointIds) {
          let matchFound = false
          
          if (dpId === 'fullDocument' && sectionTitle === 'fullDocument') {
            // This is a full document reference
            newContent = sourceNode.data?.contentMarkdown || ''
            matchFound = true
          } else if (dpId.startsWith('section:') && dpId.replace('section:', '') === sectionTitle) {
            // This is a section reference
            const sourceSectionName = dpId.replace('section:', '')
            newContent = sourceNode.data?.dataSettings?.documentSections?.sections?.[sourceSectionName]?.value || ''
            matchFound = true
          } else if (dpId.startsWith('exposed:') && dpId.replace('exposed:', '') === sectionTitle) {
            // This is an exposed data reference
            const key = dpId.replace('exposed:', '')
            newContent = sourceNode.data?.dataSettings?.exposedData?.[key]?.value || ''
            matchFound = true
          }
          
          if (matchFound) {
            sourceFound = true
            break // Exit the inner loop once we find a match
          }
        }
        
        if (sourceFound && newContent !== null) break // Exit the outer loop if we found a source
      }
    }
    
    if (sourceFound && newContent !== null) {
      console.log("sourceFound: ", sourceFound, newContent)
      // Update the section with new content
      const updatedSections = new Map(sections)
      updatedSections.set(sectionId, { ...section, content: newContent })
      
      // Get the current markdown content and split into sections
      const sectionRegex = /::: section ([^\n]+)\n([\s\S]*?)\n:::/g
      let matchCount = 0;
      let updatedMarkdown = contentMarkdown;
      
      // Replace just the content of the specific section
      updatedMarkdown = updatedMarkdown.replace(sectionRegex, (match, title, content) => {
        matchCount++;
        if (title === section.title) {
          // Only replace the content of this specific section
          return `::: section ${title}\n${newContent}\n:::`;
        }
        // Return other sections unchanged
        return match;
      });
      
      // If no sections were found or replaced, log an error
      if (matchCount === 0) {
        console.error("No sections found in the markdown content");
      }
      
      // Update content markdown - this preserves the exact order since we're only replacing content
      setContentMarkdown(updatedMarkdown)
      syncNodeData({ contentMarkdown: updatedMarkdown })
      
      // Update section metadata with current timestamp and user
      const newMeta = { ...(data.sectionMeta || {}) }
      newMeta[sectionId] = {
        updatedAt: Date.now(),
        updatedBy: user?.email || 'unknown',
      }

      syncNodeData({ sectionMeta: newMeta })
      
      console.log(`Section "${sectionTitle}" refreshed successfully.`)
    } else {
      // No source found for this section
      console.log(`Could not find original source for section "${sectionTitle}".`)
    }
    
    // Clear the status message after a few seconds
    setTimeout(() => {
      setStatusMessage('')
    }, 3000)
  }, [sections, data.protectedSections, data.setReferences, data.sectionMeta, contentMarkdown, setContentMarkdown, syncNodeData, setStatusMessage, user])

  // Ensure we're initializing sectionMeta if it doesn't exist
  useEffect(() => {
    if (!data.sectionMeta) {
      syncNodeData({ sectionMeta: {} })
    }
  }, [data.sectionMeta, syncNodeData])

  // Handle creating a new published file
  const handleCreatePublishedFile = useCallback(() => {
    if (!newPublishName.trim()) {
      setStatusMessage('Please enter a name for the published file')
      return
    }
    
    if (selectedSectionsForPublish.length === 0) {
      setStatusMessage('Please select at least one section to include')
      return
    }
    
    setStatusMessage('Generating PDF...')
    
    // Get filtered sections based on selection
    const filteredSections = Array.from(sections.entries())
      .filter(([id]) => selectedSectionsForPublish.includes(id))      .map(([id, section]) => ({ 
        id, 
        title: section.title, 
        content: section.content 
      }))
    
    // Create PDFDocument
    const pdfDocument = (
      <DocumentPDFRenderer
        sections={filteredSections}
        headerTemplate={data.headerTemplate}
        footerTemplate={data.footerTemplate}
        title={data.label || 'Untitled Document'}
        styleSettings={data.styleSettings}
        resources={data.resources}
        enableMermaidPrint={true} // Disable Mermaid in PDF exports for performance
      />
    )
    
    // Generate PDF blob
    pdf(pdfDocument).toBlob()
      .then(blob => {
        // Create a File from the blob
        const file = new File([blob], `${newPublishName}.pdf`, { type: 'application/pdf' })
        
        // Create form data for upload
        const formData = new FormData()
        formData.append('file', file)
        formData.append('userId', user?.id || '')
        
        // Upload the file
        return uploadFile(formData)
      })
      .then((response) => {
        if (response) {
          // Add to published files list
          const newPublishedFile: PublishedFile = {
            id: `pub-${Date.now()}`,
            filename: newPublishName,
            dateCreated: new Date().toISOString(),
            url: response.url,
            fileId: response.id,
            sections: selectedSectionsForPublish
          }
          
          const publishedFiles = [...(data.publishedFiles || []), newPublishedFile]
          syncNodeData({ publishedFiles })
          
          setStatusMessage(`File "${newPublishName}" published successfully`)
          setPublishDialogVisible(false)
          setNewPublishName('')
          setSelectedSectionsForPublish([])
        }
      })
      .catch((error) => {
        console.error('Error publishing file:', error)
        setStatusMessage('Error publishing file. Please try again.')
      })
  }, [newPublishName, selectedSectionsForPublish, sections, data.headerTemplate, data.footerTemplate, data.publishedFiles, data.label, data.styleSettings, data.resources, syncNodeData, setStatusMessage, uploadFile, user])

  // Handle deleting a published file
  const handleDeletePublishedFile = useCallback((fileId: string) => {
    const publishedFiles = [...(data.publishedFiles || [])]
    const fileToDelete = publishedFiles.find(file => file.id === fileId)
    
    if (fileToDelete?.fileId) {
      // First remove from published files list
      const updatedFiles = publishedFiles.filter(file => file.id !== fileId)
      syncNodeData({ publishedFiles: updatedFiles })
      
      // Note: We're not actually deleting from file system as mentioned in requirements
      setStatusMessage(`File "${fileToDelete.filename}" removed from list but kept in project files`)
    }
  }, [data.publishedFiles, syncNodeData, setStatusMessage])

  // Handle downloading a published file
  const handleDownloadPublishedFile = useCallback((fileId: string) => {
    const publishedFiles = [...(data.publishedFiles || [])]
    const fileToDownload = publishedFiles.find(file => file.id === fileId)
    
    if (fileToDownload?.fileId) {
      downloadFile(fileToDownload.fileId)
    }
  }, [data.publishedFiles, downloadFile])

  // Handle adding a resource
  const handleAddResource = useCallback((resource: Resource) => {
    const resources = [...(data.resources || []), resource]
    syncNodeData({ resources })
  }, [data.resources, syncNodeData])

  // Handle removing a resource
  const handleRemoveResource = useCallback((resourceId: string) => {
    const resources = [...(data.resources || [])]
    const updatedResources = resources.filter(resource => resource.id !== resourceId)
    syncNodeData({ resources: updatedResources })
  }, [data.resources, syncNodeData])

  // Function to create a document event handler
  const createResourceNodeEventHandler = useCallback(() => {
    const handleCreateResourceNode = (event) => {
      const { fileId, filename, nodeId, fileUrl, fileType } = event.detail
      
      // Add as a resource
      handleAddResource({
        id: nodeId,
        name: filename,
        type: fileType || 'unknown',
        url: fileUrl,
        fileId: fileId
      })
    }
    
    document.addEventListener('create-resource-node', handleCreateResourceNode)
    
    return () => {
      document.removeEventListener('create-resource-node', handleCreateResourceNode)
    }
  }, [handleAddResource])

  // Set up event listener for resource node creation
  useEffect(() => {
    const cleanup = createResourceNodeEventHandler()
    return cleanup
  }, [createResourceNodeEventHandler])

  // Customized section renderer - wrap MarkdownRenderer with drag handles
  const renderSections = useCallback(() => {
    const sortedSections = getSortedSections()
    const disabledSections = data.disabledSections || {}
    const protectedSections = data.protectedSections || {}
    
    return sortedSections.map(([sectionId, section], index) => {
      const isDisabled = !!disabledSections[sectionId]
      const isProtected = !!protectedSections[sectionId]
      
      // Create section-specific metadata object
      const sectionMetaForRenderer: Record<string, { updatedAt?: number; updatedBy?: string }> = {}
      
      // If we have metadata for this section, include it with the correct mapping
      if (data.sectionMeta?.[sectionId]) {
        // Use 'section-1' as the key because that's what MarkdownRenderer generates internally
        // when rendering a single section from the input markdown
        sectionMetaForRenderer['section-1'] = data.sectionMeta[sectionId]
      }
      
      return (
        <div 
          key={sectionId}
          className={`${styles.sectionWrapper} ${isDisabled ? styles.disabledSection : ''}`}
          onContextMenu={(e) => handleSectionContextMenu(e, sectionId, section.title)}
          data-nodrag
        >
          <div className={`${styles.sectionHandle} ${isDisabled ? styles.sectionHandleDisabled : ''}`}>
            <div className={styles.sectionMoveButtons}>
              <button 
                className={styles.sectionMoveButton}
                onClick={() => handleMoveSection(sectionId, 'up')}
                disabled={index === 0 || isDisabled}
              >
                <ArrowUp size={16} />
              </button>
              
              <GripVertical size={16} className="text-gray-400 my-2" />
              
              <button 
                className={styles.sectionMoveButton}
                onClick={() => handleMoveSection(sectionId, 'down')}
                disabled={index === sortedSections.length - 1 || isDisabled}
              >
                <ArrowDown size={16} />
              </button>
            </div>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.sectionActionsContainer}>
              <button 
                className={styles.actionButton}
                onClick={() => handleRefreshSection(sectionId)}
                title="Refresh section data"
                disabled={isDisabled}
              >
                <RefreshCcw size={16} className="text-blue-500 hover:text-blue-700" />
              </button>
              
              <button 
                className={styles.actionButton}
                onClick={() => handleToggleSectionProtected(sectionId, !isProtected)}
                title={isProtected ? "Remove protection" : "Protect section"}
              >
                <Shield size={16} className={isProtected ? "text-green-500" : "text-gray-400"} />
              </button>
              
              <button 
                className={styles.actionButton}
                onClick={() => handleToggleSectionDisabled(sectionId)}
                title={isDisabled ? "Enable section" : "Disable section"}
              >
                {isDisabled ? <Eye size={16} className="text-gray-500" /> : <EyeOff size={16} className="text-gray-500" />}
              </button>
            </div>              <MarkdownRenderer 
              markdown={`::: section ${section.title}\n${section.content}\n:::`}
              nodeId={id}
              sectionStates={sectionStates}
              sectionReviews={sectionReviews}
              onAcceptReview={handleAcceptReview}
              onRejectReview={handleRejectReview}
              disableSections={false}
              protectedSections={protectedSections}
              disabledSections={disabledSections}
              sectionMeta={sectionMetaForRenderer}
              key={sectionId} // Use stable sectionId as key instead of activeTab
            />
          </div>
        </div>
      )
    })
  }, [
    getSortedSections, 
    data.disabledSections, 
    data.protectedSections,
    data.sectionMeta, // Add sectionMeta dependency
    handleSectionContextMenu, 
    handleMoveSection,
    handleRefreshSection,
    handleToggleSectionProtected,
    handleToggleSectionDisabled,
    sectionStates,
    sectionReviews,
    handleAcceptReview,
    handleRejectReview,
    id
  ])

  // Copy markdown to clipboard
  const handleCopyToClipboard = () => copyToClipboard(contentMarkdown, markdownPreviewRef, setCopyFeedback)

  // Custom buttons for the node header
  const customButtons = [
    { id: '1', onClick: () => setFileDialogVisible(true), icon: <Files size={16} />, tooltip: 'Manage Resources' },
    { id: '2', onClick: () => setPublishDialogVisible(true), icon: <FileOutput size={16} />, tooltip: 'Publish Document' },
    { id: '3', onClick: () => setHeaderFooterDialogVisible(true), icon: <LayoutTemplate size={16} />, tooltip: 'Header & Footer Settings' },
    { id: '4', onClick: () => { setIsUpdateWithAIDialogVisible(true); }, icon: <RefreshCw size={16} />, tooltip: 'Update with AI' },
    { id: '5', onClick: () => setIntroEnabled(true), icon: <HelpCircle size={16} />, tooltip: 'Help & Guide' },
  ]

  // Function to update header or footer
  const updateHeaderFooter = (type: 'header' | 'footer', content: string) => {
    if (type === 'header') {
      syncNodeData({ headerTemplate: content })
    } else {
      syncNodeData({ footerTemplate: content })
    }
  }

  // Handle section state change from context menu
  const handleSectionStateChangeFromMenu = (state: 'Draft' | 'Detailed' | 'Styled') => {
    if (contextMenu.sectionId) {
      handleSectionStateChange(contextMenu.sectionId, state)
      setContextMenu({ ...contextMenu, visible: false })
    }
  }

  // Handle section protection change from context menu
  const handleSectionProtectedChange = (protectedStatus: boolean) => {
    if (contextMenu.sectionId) {
      handleToggleSectionProtected(contextMenu.sectionId, protectedStatus)
      setContextMenu({ ...contextMenu, visible: false })
    }
  }

  // Add missing handleMinimizeLevelChange function
  const handleMinimizeLevelChange = (level: 'normal' | 'medium' | 'minimum') => {
    setMinimizeLevel(level)
    setIsMinimized(level !== 'normal')
    updateNodeData(id, { 
      ...data,
      minimizeLevel: level,
      isMinimized: level !== 'normal'
    })
  }

  // Configuration options specific to NodeComposite
  const compositeConfigContent = (config: NodeConfiguration, setConfig: (config: NodeConfiguration) => void) => (
    <div className="space-y-4 mt-4">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="markdownFormatVisible"
          checked={config.markdownFormatVisible}
          onCheckedChange={(checked) => setConfig({ ...config, markdownFormatVisible: checked === true })}
        />
        <Label htmlFor="markdownFormatVisible">
          Show Markdown format tab
        </Label>
      </div>
      
      <div className="flex items-center space-x-2">
        <Checkbox
          id="showPublishedFiles"
          checked={(config as CompositeNodeConfiguration).showPublishedFiles}
          onCheckedChange={(checked) => 
            setConfig({ 
              ...config, 
              showPublishedFiles: checked === true 
            } as CompositeNodeConfiguration)
          }
        />
        <Label htmlFor="showPublishedFiles">
          Show published files panel
        </Label>
      </div>
      
      <div className="flex items-center space-x-2">
        <Checkbox
          id="enableDragAndDrop"
          checked={(config as CompositeNodeConfiguration).enableDragAndDrop}
          onCheckedChange={(checked) => 
            setConfig({ 
              ...config, 
              enableDragAndDrop: checked === true 
            } as CompositeNodeConfiguration)
          }
        />
        <Label htmlFor="enableDragAndDrop">
          Enable section drag and drop
        </Label>
      </div>
      
      <div className="flex items-center space-x-2">
        <Checkbox
          id="autoRefreshSections"
          checked={(config as CompositeNodeConfiguration).autoRefreshSections}
          onCheckedChange={(checked) => 
            setConfig({ 
              ...config, 
              autoRefreshSections: checked === true 
            } as CompositeNodeConfiguration)
          }
        />
        <Label htmlFor="autoRefreshSections">
          Auto-refresh sections on data change
        </Label>
      </div>
    </div>
  )

  // Build availableNodes dynamically from flow
  const availableNodes = React.useMemo(() => {
    const allNodes = (window as any).__flowGetAllNodes?.() || []
    return allNodes
      .filter((n: any) => n.id !== id && n.data)
      .map((n: any) => {
        const dataPoints: { id: string; label: string; description?: string }[] = []

        // Add full document if exists
        if (n.data?.dataSettings?.documentSections?.fullDocument) {
          dataPoints.push({ id: 'fullDocument', label: 'Full Document' })
        }

        // Add all sections if exist
        const sections = n.data?.dataSettings?.documentSections?.sections || {}
        Object.entries(sections).forEach(([title, section]: [string, any]) => {
          dataPoints.push({ id: `section:${title}`, label: title })
        })

        // Add all exposed data points if exist
        const exposedData = n.data?.dataSettings?.exposedData || {}
        Object.entries(exposedData).forEach(([key, field]: [string, any]) => {
          dataPoints.push({
            id: `exposed:${key}`,
            label: field.label || key,
            description: field.description || '',
          })
        })

        return {
          id: n.id,
          label: n.data?.label || n.id,
          dataPoints,
        }
      })
  }, [id])

  // Handle saving references from dialog
  const handleSaveReferences = useCallback((selected: Record<string, string[]>, isLoading = false) => {
    const allNodes = (window as any).__flowGetAllNodes?.() || []

    // Extract all existing sections
    const existingSections: Record<string, string> = {}
    const sectionRegex = /::: section ([^\n]+)\n([\s\S]*?)\n:::/g
    let match
    while ((match = sectionRegex.exec(contentMarkdown)) !== null) {
      const sid = match[1].trim()
      const content = match[2]
      existingSections[sid] = content
    }

    let combined = ''
    const newSectionOrder: string[] = []

    Object.entries(selected).forEach(([nodeId, dataPointIds]) => {
      const node = allNodes.find((n: any) => n.id === nodeId)
      if (!node) return

      dataPointIds.forEach((dpId) => {
        let content = ''
        let sectionHeader = dpId

        if (dpId === 'fullDocument') {
          content = node.data?.contentMarkdown || ''
          sectionHeader = 'fullDocument'
        } else if (dpId.startsWith('section:')) {
          const sectionName = dpId.replace('section:', '')
          content = node.data?.dataSettings?.documentSections?.sections?.[sectionName]?.value || ''
          sectionHeader = sectionName
        } else if (dpId.startsWith('exposed:')) {
          const key = dpId.replace('exposed:', '')
          content = node.data?.dataSettings?.exposedData?.[key]?.value || ''
          sectionHeader = key
        }

        // Check for protection by any associated identifier
        const isProtected = Object.entries(data.protectedSections || {}).some(([key, isProtected]) => {
          // Check if this section matches any protected pattern
          return isProtected && (
            key === sectionHeader || 
            key === `section-${sectionHeader}`
          );
        });

        // Use existing content if protected and exists
        if (isProtected && existingSections[sectionHeader] !== undefined) {
          combined += `\n::: section ${sectionHeader}\n${existingSections[sectionHeader]}\n:::\n`
        } else {
          combined += `\n::: section ${sectionHeader}\n${content}\n:::\n`
        }
        
        // Find appropriate section ID
        const sectionId = `section-${newSectionOrder.length + 1}`;
        newSectionOrder.push(sectionId);
      })
    })

    syncNodeData({
      contentMarkdown: combined,
      sectionOrder: newSectionOrder,
      ...(isLoading ? {} : { setReferences: selected }),
    })
    
    // Show success message
    setStatusMessage('Sections imported successfully')
    setTimeout(() => setStatusMessage(''), 3000)
  }, [contentMarkdown, data.protectedSections, syncNodeData, setStatusMessage])

  // Add refs collection for content areas
const contentRefs = useRef<{
  composite: HTMLDivElement | null;
  resources: HTMLDivElement | null;
  published: HTMLDivElement | null;
  markdown: HTMLTextAreaElement | null; // Update type if needed
  printable: HTMLDivElement | null;
}>({
  composite: null,
  resources: null,
  published: null,
  markdown: null,
  printable: null,
});
    // Add state for active tab
  const [activeTab, setActiveTab] = useState('composite');
  
  // Add effect to track tab changes and ensure Mermaid re-rendering
  useEffect(() => {
    console.log('NodeComposite activeTab changed to:', activeTab);
    
    // Force Mermaid diagrams to re-render when switching tabs
    setTimeout(() => {
      const pendingMermaidElements = document.querySelectorAll('.mermaid-diagram[data-render-state="pending"]');
      console.log(`Found ${pendingMermaidElements.length} pending Mermaid diagrams after tab switch to ${activeTab}`);
      
      // Also check for already rendered diagrams
      const renderedMermaidElements = document.querySelectorAll('.mermaid-diagram[data-render-state="rendered"]');
      console.log(`Found ${renderedMermaidElements.length} already rendered Mermaid diagrams in ${activeTab} tab`);
      
      // Check if DOM elements are actually visible
      pendingMermaidElements.forEach((el, index) => {
        const rect = el.getBoundingClientRect();
        console.log(`Pending Mermaid ${index}: visible=${rect.width > 0 && rect.height > 0}, bounds=${JSON.stringify(rect)}`);
      });
    }, 100);
  }, [activeTab]);
  
useEffect(() => {
  const attachScrollListeners = () => {
    const handleWheel = (e: WheelEvent) => {
      const target = e.currentTarget as HTMLElement | HTMLTextAreaElement;
      const isMiddleMouse = e.buttons === 4;
      if (handleScroll(target, e.deltaY, isMiddleMouse ? 4 : 0)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const elements: (HTMLElement | HTMLTextAreaElement)[] = [];

    // Update contentRefs based on current tab
    if (activeTab === 'composite' && contentRefs.current.composite) {
      elements.push(contentRefs.current.composite);
    } else if (activeTab === 'markdown' && textareaRef.current) {
      contentRefs.current.markdown = textareaRef.current;
      elements.push(contentRefs.current.markdown);
    } else if (activeTab === 'resources' && contentRefs.current.resources) {
      elements.push(contentRefs.current.resources);
    } else if (activeTab === 'published' && contentRefs.current.published) {
      elements.push(contentRefs.current.published);
    } else if (activeTab === 'printable' && contentRefs.current.printable) {
      elements.push(contentRefs.current.printable);
    }

    const validElements = elements.filter(el => el !== null && el !== undefined);    console.log(
      `NodeComposite: Attaching scroll listeners to ${validElements.length} elements for tab: ${activeTab}`,
      validElements.map(el => ({
        tagName: el.tagName,
        className: el.className,
        id: el.id || 'no-id',
        isConnected: el.isConnected,
        offsetParent: el.offsetParent?.tagName || 'null'
      }))
    );

    validElements.forEach((el) => {
      el.addEventListener('wheel', handleWheel, { passive: false });
    });

    return () => {
      validElements.forEach((el) => {
        el.removeEventListener('wheel', handleWheel);
      });
    };
  };

  // Attach immediately
  let cleanup = attachScrollListeners();

  // Retry after a short delay to catch late mounts
  const timeout = setTimeout(() => {
    cleanup();
    cleanup = attachScrollListeners();
  }, 100);

  return () => {
    clearTimeout(timeout);
    cleanup();
  };
}, [activeTab]);

  // Clean markdown for printable view
  const cleanMarkdownForPrinting = (markdown: string) => {
    return markdown
      .replace(/:::\s*section\s+.*$/gm, '')
      .replace(/^:::$/gm, '')
      .replace(/\n\n\n+/gm, '\n\n')
      .trim();
  };
  // Print function similar to DocumentTabs handlePrint
  const handleCompositeNodePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const printableContent = document.getElementById('composite-printable-content')?.innerHTML;
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Document - ${data.label || 'Untitled Composite'}</title>
            <style>
              body {
                font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                line-height: 1.5;
                padding: 2rem;
                max-width: 800px;
                margin: 0 auto;
              }
              h1, h2, h3, h4, h5, h6 { margin-top: 1.5em; margin-bottom: 0.5em; }
              p { margin-bottom: 1em; }
              ul, ol { margin-bottom: 1em; padding-left: 2em; }
              pre { background-color: #f5f5f5; padding: 1em; border-radius: 4px; overflow-x: auto; }
              code { font-family: monospace; background-color: #f5f5f5; padding: 0.2em 0.4em; border-radius: 3px; }
              blockquote { border-left: 4px solid #ddd; padding-left: 1em; margin-left: 0; color: #666; }
              img { max-width: 100%; height: auto; }
              table { border-collapse: collapse; width: 100%; margin-bottom: 1em; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f5f5f5; }
              /* Mermaid diagram styles */
              .mermaid { 
                margin: 1em 0; 
                text-align: center; 
                page-break-inside: avoid; 
              }
              .mermaid svg { 
                max-width: 100%; 
                height: auto; 
                display: block; 
                margin: 0 auto; 
              }
              /* Ensure Mermaid content is visible in print */
              .mermaid-diagram {
                margin: 1em 0;
                text-align: center;
                page-break-inside: avoid;
              }
              .mermaid-diagram svg {
                max-width: 100%;
                height: auto;
                display: block;
                margin: 0 auto;
              }
              @media print { 
                body { padding: 0; }
                .mermaid, .mermaid-diagram { 
                  page-break-inside: avoid; 
                  -webkit-print-color-adjust: exact;
                  color-adjust: exact;
                }
                svg {
                  -webkit-print-color-adjust: exact;
                  color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            ${printableContent || ''}
            <script>
              window.onload = function() { 
                // Wait for any images/diagrams to load before printing
                setTimeout(() => {
                  window.print(); 
                  window.setTimeout(() => window.close(), 500); 
                }, 2000); // Increased timeout to ensure Mermaid diagrams are rendered
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };
  // Update the handlePrint function to respect section order (this is actually for publishing)
  const handlePublish = async () => {
    // Get filtered sections (all non-disabled sections)
    const filteredSections = getSortedSections()
      .filter(([id]) => !data.disabledSections?.[id])
      .map(([id, section]) => ({ 
        id, 
        title: section.title, 
        content: section.content 
      }));

    // Set status while generating
    setStatusMessage('Processing Mermaid diagrams and generating PDF...');

    try {
      // Pre-process sections to handle Mermaid diagrams
      const processedSections = await Promise.all(
        filteredSections.map(async (section) => {
          const processedContent = await processMermaidInContent(section.content);
          return {
            ...section,
            content: processedContent
          };
        })
      );

      // Create PDFDocument with processed content
      const pdfDocument = (
        <DocumentPDFRenderer
          sections={processedSections}
          headerTemplate={data.headerTemplate}
          footerTemplate={data.footerTemplate}
          title={data.label || 'Untitled Document'}
          styleSettings={data.styleSettings}
          resources={data.resources}
          enableMermaidPrint={true} // Now we can enable since content is pre-processed
        />
      );      // Generate PDF blob
      const blob = await pdf(pdfDocument).toBlob();
      
      // Create a File from the blob
      const file = new File([blob], `${newPublishName}.pdf`, { type: 'application/pdf' });
      
      // Create form data for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', user?.id || '');
      
      // Upload the file
      const response = await uploadFile(formData);
      
      if (response) {
        // Add to published files list
        const newPublishedFile: PublishedFile = {
          id: `pub-${Date.now()}`,
          filename: newPublishName,
          dateCreated: new Date().toISOString(),
          url: response.url,
          fileId: response.id,
          sections: selectedSectionsForPublish
        };
        
        const publishedFiles = [...(data.publishedFiles || []), newPublishedFile];
        syncNodeData({ publishedFiles });
        
        setStatusMessage(`File "${newPublishName}" published successfully`);
        setPublishDialogVisible(false);
        setNewPublishName('');
        setSelectedSectionsForPublish([]);
      }
    } catch (error) {
      console.error('Error publishing file:', error);
      setStatusMessage('Error publishing file. Please try again.');
    }
  };

  const nodeStatus = useMemo(() => {
    // Add your logic here based on your node's AI/processing state
    if (data?.statusMessage?.toLowerCase().includes("loading")) return "loading";
    else if (data?.statusMessage?.toLowerCase().includes("error")) return "error";
    else if (data?.statusMessage?.toLowerCase().includes("complete")) return "success";
    else if (data?.statusMessage?.toLowerCase().includes("successful")) return "success";
    return "initial";
  }, [data]);
  // Function to process Mermaid diagrams in content and replace with placeholders
  const processMermaidInContent = async (content: string): Promise<string> => {
    const mermaidRegex = /```mermaid\s+([\s\S]*?)\s+```/g;
    let processedContent = content;
    let match;

    while ((match = mermaidRegex.exec(content)) !== null) {
      const fullMatch = match[0];
      const mermaidCode = match[1].trim();
      
      // Create a placeholder that explains Mermaid is processed for PDF
      const placeholder = `
**Mermaid Diagram (${mermaidCode.split('\n')[0].trim()})**

*This diagram has been processed for PDF export. For interactive diagrams, use the printable view.*

\`\`\`
${mermaidCode.substring(0, 100)}${mermaidCode.length > 100 ? '...' : ''}
\`\`\`
`;

      processedContent = processedContent.replace(fullMatch, placeholder);
    }

    return processedContent;
  };

  return (
    <>
      <NodeBaseContainer
        resizable={!isLocked && !data.documentCommand?.processing}
        minWidth={400}
        minHeight={300}
        id={id}
        data={data}
        isProcessing={data.documentCommand?.processing || false}
        ref={nodeBaseContainerRef}
        className={`${styles.markdownPreview} ${isMarkdownExpanded ? 'flex-1 min-h-0' : 'h-[600px]'}`}
        nodeTitle={data.label || 'Untitled Composite'}
        isLocked={isLocked}
        isMinimized={isMinimized}
        onLockChange={handleLock}
        onMinimizeChange={handleMinimize}
        onTitleChange={(title) => syncNodeData({ label: title })}
        customButtons={customButtons}
        assignedEmails={Array.isArray(assignedEmails) ? assignedEmails : (assignedEmails ? assignedEmails.trim().split(/\s+/).filter(Boolean) : [])}
        onAssignUsersClick={() => {
          const emailsStr = window.prompt('Enter emails separated by space', Array.isArray(assignedEmails) ? assignedEmails.join(' ') : (assignedEmails || ''))
          if (emailsStr !== null) {
            const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
            setAssignedEmails(emailsArr)
            syncNodeData({ assignedEmails: emailsArr })
          }
        }}
        onDataSettingsClick={handleOpenDataSettingsDialog}
        onConfigClick={() => setIsConfigDialogVisible(true)}
        onToggleDebugInfo={() => setShowDebugInfo((prev) => !prev)}
        showDebugInfo={showDebugInfo}
        status={nodeStatus}
        minimizeLevel={minimizeLevel}
        onMinimizeLevelChange={handleMinimizeLevelChange}
        selected={selected} // ✅ CRITICAL: Add the missing selected prop!
      >
        {(minimizeLevel || 'normal') === 'normal' && (
          <div className="flex flex-col h-full">
            {/* All existing content goes here */}
            <div className={styles.nodeComposite}>
              <div className={styles.nodeContent}>
                {!isLocked && !data.documentCommand?.processing && <NodeTags />}
                {!isLocked && !data.documentCommand?.processing && <NodeExports />}
                
                <div className={styles.nodeMainContent} style={{ flex: '1 1 auto', maxHeight: 'calc(100% - 40px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  {!isMinimized && (
                    <div className={`${styles.contentStack} w-full h-full`} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div className={styles.functionBar}>
                        <Button size="sm" variant="outline" onClick={() => setIsReferenceDialogVisible(true)}>
                          Set References
                        </Button>
                        <DocumentStyleSelector
                          documentStyle={documentStyle}
                          onStyleChange={handleStyleChange}
                          disabled={isLocked || data.documentCommand?.processing}
                        />
                        <Button size="sm" variant="outline" onClick={handleCopyToClipboard}>
                          <Clipboard className="mr-2 h-4 w-4" /> Copy
                        </Button>
                        <Button size="sm" variant="outline" onClick={toggleMarkdownExpand}>
                          {isMarkdownExpanded ? <><Minimize className="mr-2 h-4 w-4" />Collapse</> : <><Expand className="mr-2 h-4 w-4" />Expand</>}
                        </Button>
                        {/* --- Add these two buttons next to Expand/Collapse --- */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setHeaderFooterDialogVisible(true)}
                          style={{ marginLeft: 4 }}
                          title="Header & Footer Settings"
                        >
                          <LayoutTemplate className="mr-2 h-4 w-4" />
                          Header & Footer
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPublishDialogVisible(true)}
                          style={{ marginLeft: 4 }}
                          title="Publish Document"
                        >
                          <FileOutput className="mr-2 h-4 w-4" />
                          Publish
                        </Button>
                      </div>
                      
                      <Tabs 
                        defaultValue="composite" 
                        className="w-full flex flex-col flex-1 h-full"
                        value={activeTab}
                        onValueChange={setActiveTab}
                      >
                        <TabsList className="w-full">
                          <TabsTrigger value="composite" className="flex-1">Composite Document</TabsTrigger>
                          <TabsTrigger value="resources" className="flex-1">Resources</TabsTrigger>
                          {(data.configuration)?.showPublishedFiles && (
                            <TabsTrigger value="published" className="flex-1">Published Files</TabsTrigger>
                          )}
                          {(data.configuration)?.markdownFormatVisible && (
                            <TabsTrigger value="markdown" className="flex-1">Markdown</TabsTrigger>
                          )}
                          <TabsTrigger value="printable" className="flex-1">Printable</TabsTrigger>
                        </TabsList>

                                                  <TabsContent value="markdown" className={styles.tabsContentContainer}>
                            <Textarea
                              ref={textareaRef}
                              value={contentMarkdown}
                              onChange={(e) => handleContentChange(e.target.value)}
                              className="w-full h-full font-mono"
                              placeholder="Enter markdown content..."
                              onWheel={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          </TabsContent>

                        <TabsContent value="composite" className={styles.tabsContentContainer}>

                            <div 
                              className={`${styles.markdownPreview} scrollable-content p-4`}
                              ref={(el) => {
                                if (el) {
                                  contentRefs.current.composite = el;
                                }
                              }}
                              style={{
                                maxHeight: 'auto',
                                border: '1px solid #ccc',
                                padding: '8px',
                                overflowY: 'auto',
                              }}
                            >
                              {renderSections()}
                            </div>

                        </TabsContent>
                        
                        <TabsContent value="printable" className={styles.tabsContentContainer}>
                          <div className="p-3">
                            <div className="flex justify-between mb-2">
                              <div className="text-sm text-gray-500">
                                Clean document view for printing or exporting as PDF.
                              </div>
                              <Button onClick={handleCompositeNodePrint} variant="outline" size="sm">
                                <Printer className="mr-2 h-4 w-4" />
                                Print / PDF
                              </Button>
                            </div>
                            <ScrollArea 
                              className={styles.scrollableContent}
                              onWheel={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <div 
                                className={`${styles.markdownPreview} border rounded p-4`}
                                style={{
                                  backgroundColor: 'white',
                                  minHeight: '100%'
                                }}
                                ref={(el) => {
                                  if (el) {
                                    // Find the ScrollArea viewport
                                    const scrollArea = el.parentElement?.parentElement;
                                    const viewport = scrollArea?.querySelector('[data-radix-scroll-area-viewport]')!;
                                    if (viewport) {
                                      contentRefs.current.printable = viewport;
                                    }
                                  }
                                }}
                              >
                                <div id="composite-printable-content" className="font-sans">
                                  {/* Render header if available */}
                                  {data.headerTemplate && (
                                    <div className="mb-8 pb-4 border-b">
                                      <MarkdownRenderer 
                                        markdown={data.headerTemplate
                                          .replace(/\[Document Title\]/g, data.label || 'Untitled Document')
                                          .replace(/\[Current Date\]/g, new Date().toLocaleDateString())
                                          .replace(/\[Current Year\]/g, new Date().getFullYear().toString())}
                                        disableSections={true}
                                      />
                                    </div>
                                  )}
                                  
                                  {/* Render document content */}
                                  <div className="my-6">
                                    {getSortedSections()
                                      .filter(([sectionId]) => !data.disabledSections?.[sectionId])
                                      .map(([_, section]) => (
                                        <MarkdownRenderer 
                                          key={section.title}
                                          markdown={`# ${section.title}\n\n${section.content}`}
                                          disableSections={true} 
                                        />
                                      ))}
                                  </div>
                                  
                                  {/* Render footer if available */}
                                  {data.footerTemplate && (
                                    <div className="mt-8 pt-4 border-t">
                                      <MarkdownRenderer 
                                        markdown={data.footerTemplate
                                          .replace(/\[Document Title\]/g, data.label || 'Untitled Document')
                                          .replace(/\[Current Date\]/g, new Date().toLocaleDateString())
                                          .replace(/\[Current Year\]/g, new Date().getFullYear().toString())}
                                        disableSections={true}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </ScrollArea>
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="resources" className={styles.tabsContentContainer}>
                        <div className="flex justify-between p-2">
                          <h3 className="text-lg font-medium">Resources</h3>
                          <Button onClick={() => setFileDialogVisible(true)} variant="outline" size="sm">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Resource
                          </Button>
                        </div>
                        
                        <ScrollArea 
                          className={styles.scrollableContent}
                          onWheel={(e) => {
                            e.stopPropagation();
                          }}
                          data-nodrag
                        >
                          <div
                            ref={(el) => {
                              if (el) {
                                // Find the ScrollArea viewport
                                const scrollArea = el.parentElement?.parentElement;
                                const viewport = scrollArea?.querySelector('[data-radix-scroll-area-viewport]')!;
                                if (viewport) {
                                  contentRefs.current.resources = viewport;
                                }
                              }
                            }}
                          >
                            {data.resources && data.resources.length > 0 ? (
                              <div className="p-4 space-y-2">
                                {data.resources.map((resource) => (
                                  <div key={resource.id} className="flex items-center justify-between p-2 border rounded-md">
                                    <div className="flex items-center">
                                      {resource.type.startsWith('image/') ? (
                                        <Image className="h-4 w-4 mr-2" />
                                      ) : (
                                        <FileText className="h-4 w-4 mr-2" />
                                      )}
                                      <span>{resource.name}</span>
                                    </div>
                                    <div className="flex space-x-1">
                                      {resource.url && (
                                        <Button variant="ghost" size="icon" onClick={() => window.open(resource.url, '_blank')}>
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                      )}
                                      <Button variant="ghost" size="icon" onClick={() => handleRemoveResource(resource.id)}>
                                        <Trash className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center h-full p-4 text-center text-gray-500">
                                <FileText className="h-16 w-16 mb-4 opacity-20" />
                                <p>No resources added yet.</p>
                                <p className="text-sm mt-2">Click "Add Resource" to upload files.</p>
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                      </TabsContent>
                        
                        {(data.configuration)?.showPublishedFiles && (
                          <TabsContent value="published" className={styles.tabsContentContainer}>
                            <div className="flex justify-between p-2">
                              <h3 className="text-lg font-medium">Published Files</h3>
                              <Button onClick={() => setPublishDialogVisible(true)} variant="outline" size="sm">
                                <FilePlus className="h-4 w-4 mr-2" />
                                Publish New
                              </Button>
                            </div>
                            
                            <ScrollArea 
                              className={styles.scrollableContent}
                              onWheel={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <div
                                ref={(el) => {
                                  if (el) {
                                    // Find the ScrollArea viewport
                                    const scrollArea = el.parentElement?.parentElement;
                                    const viewport = scrollArea?.querySelector('[data-radix-scroll-area-viewport]')!;
                                    if (viewport) {
                                      contentRefs.current.published = viewport;
                                    }
                                  }
                                }}
                              >
                                {data.publishedFiles && data.publishedFiles.length > 0 ? (
                                  <div className="p-4 space-y-2">
                                    {data.publishedFiles.map((file) => (
                                      <div key={file.id} className={styles.publishedFileItem}>
                                        <div>
                                          <div className="font-medium">{file.filename}</div>
                                          <div className="text-sm text-gray-500">
                                            Published: {new Date(file.dateCreated).toLocaleDateString()}
                                          </div>
                                          <div className="text-xs text-gray-400">
                                            {file.sections.length} sections
                                          </div>
                                        </div>
                                        <div className={styles.publishedFileActions}>
                                          <Button variant="ghost" size="icon" onClick={() => handleDownloadPublishedFile(file.id)}>
                                            <Download className="h-4 w-4" />
                                          </Button>
                                          <Button variant="ghost" size="icon" onClick={() => handleDeletePublishedFile(file.id)}>
                                            <Trash className="h-4 w-4" />
                                          </Button>
                                          <Button variant="ghost" size="icon" onClick={() => window.open(file.url, '_blank')}>
                                            <Eye className="h-4 w-4" />
                                          </Button>
                                          <Button variant="ghost" size="icon">
                                            <Printer className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center justify-center h-full p-4 text-center text-gray-500">
                                    <FileOutput className="h-16 w-16 mb-4 opacity-20" />
                                    <p>No published files yet.</p>
                                    <p className="text-sm mt-2">Click "Publish New" to create a PDF.</p>
                                  </div>
                                )}
                              </div>
                            </ScrollArea>
                          </TabsContent>
                        )}
                        
                        {(data.configuration)?.markdownFormatVisible && (
                          <TabsContent value="markdown" className={styles.tabsContentContainer}>
                            <Textarea
                              ref={textareaRef}
                              value={contentMarkdown}
                              onChange={(e) => handleContentChange(e.target.value)}
                              className="w-full h-full font-mono"
                              placeholder="Enter markdown content..."
                              onWheel={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          </TabsContent>
                        )}
                      </Tabs>
                      
                      {statusMessage && <div className={styles.statusLabel}>{statusMessage}</div>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </NodeBaseContainer>

      {/* Context Menu for Sections */}
      <SectionContextMenu
        isVisible={contextMenu.visible}
        top={contextMenu.y}
        left={contextMenu.x}
        nodeId={id}
        sectionId={contextMenu.sectionId}
        onStateChange={handleSectionStateChangeFromMenu}
        onProtectChange={handleSectionProtectedChange}
        isProtected={data.protectedSections?.[contextMenu.sectionId] || false}
      />

      {/* File Resourcing Dialog */}
      <FileResourcingDialog
        visible={fileDialogVisible}
        onHide={() => setFileDialogVisible(false)}
        userId={user?.id}
      />

      {/* Publish Document Dialog */}
      <PDFExportDialog
        visible={publishDialogVisible}
        onHide={() => setPublishDialogVisible(false)}
        onSave={(filename, blob) => {
          // Create a File from the blob
          const file = new File([blob], `${filename}.pdf`, { type: 'application/pdf' })
          
          // Create form data for upload
          const formData = new FormData()
          formData.append('file', file)
          formData.append('userId', user?.id || '')
          
          // Upload the file
          uploadFile(formData)
            .then((response) => {
              if (response) {
                // Add to published files list
                const newPublishedFile: PublishedFile = {
                  id: `pub-${Date.now()}`,
                  filename,
                  dateCreated: new Date().toISOString(),
                  url: response.url,
                  fileId: response.id,
                  sections: selectedSectionsForPublish
                }
                
                const publishedFiles = [...(data.publishedFiles || []), newPublishedFile]
                syncNodeData({ publishedFiles })
                
                setStatusMessage(`File "${filename}" published successfully`)
                setSelectedSectionsForPublish([])
              }
            })
            .catch((error) => {
              console.error('Error publishing file:', error)
              setStatusMessage('Error publishing file. Please try again.')
            })
        }}
        title={data.label || 'Untitled Document'}
        sections={getSortedSections().map(([id, section]) => ({
          id,
          title: section.title,
          content: section.content
        }))}
        headerTemplate={data.headerTemplate}
        footerTemplate={data.footerTemplate}
        styleSettings={data.styleSettings}
        resources={data.resources}
        disabledSections={data.disabledSections || {}}
      />

      {/* Header & Footer Settings Dialog */}
      <Dialog open={headerFooterDialogVisible} onOpenChange={setHeaderFooterDialogVisible}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Header & Footer Settings</DialogTitle>
            <DialogDescription>
              Customize the header and footer for your published documents.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <div className={styles.panelHeader}>
                <Label htmlFor="header-template">Document Header</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setEditingHeaderFooter('header')
                  }}
                >
                  Edit
                </Button>
              </div>
              <div className={styles.headerFooterPreview}>
                <MarkdownRenderer 
                  markdown={data.headerTemplate || ''}
                  disableSections={true}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className={styles.panelHeader}>
                <Label htmlFor="footer-template">Document Footer</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setEditingHeaderFooter('footer')
                  }}
                >
                  Edit
                </Button>
              </div>
              <div className={styles.headerFooterPreview}>
                <MarkdownRenderer 
                  markdown={data.footerTemplate || ''}
                  disableSections={true}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Page Style Settings</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="font-family">Font Family</Label>
                  <Select 
                    value={data.styleSettings?.fontFamily || 'Times New Roman'}
                    onValueChange={(value) => {
                      syncNodeData({ 
                        styleSettings: {
                          ...data.styleSettings,
                          fontFamily: value
                        }
                      })
                    }}
                  >
                    <SelectTrigger id="font-family">
                      <SelectValue placeholder="Select font" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                      <SelectItem value="Arial">Arial</SelectItem>
                      <SelectItem value="Helvetica">Helvetica</SelectItem>
                      <SelectItem value="Courier">Courier</SelectItem>
                      <SelectItem value="Calibri">Calibri</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="font-size">Font Size</Label>
                  <Select 
                    value={data.styleSettings?.fontSize || '12pt'}
                    onValueChange={(value) => {
                      syncNodeData({ 
                        styleSettings: {
                          ...data.styleSettings,
                          fontSize: value
                        }
                      })
                    }}
                  >
                    <SelectTrigger id="font-size">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10pt">10pt</SelectItem>
                      <SelectItem value="11pt">11pt</SelectItem>
                      <SelectItem value="12pt">12pt</SelectItem>
                      <SelectItem value="14pt">14pt</SelectItem>
                      <SelectItem value="16pt">16pt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="page-size">Page Size</Label>
                  <Select 
                    value={data.styleSettings?.pageSize || 'A4'}
                    onValueChange={(value) => {
                      syncNodeData({ 
                        styleSettings: {
                          ...data.styleSettings,
                          pageSize: value
                        }
                      })
                    }}
                  >
                    <SelectTrigger id="page-size">
                      <SelectValue placeholder="Select page size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A4">A4</SelectItem>
                      <SelectItem value="Letter">Letter</SelectItem>
                      <SelectItem value="Legal">Legal</SelectItem>
                      <SelectItem value="A3">A3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="orientation">Orientation</Label>
                  <Select 
                    value={data.styleSettings?.orientation || 'portrait'}
                    onValueChange={(value) => {
                      syncNodeData({ 
                        styleSettings: {
                          ...data.styleSettings,
                          orientation: value
                        }
                      })
                    }}
                  >
                    <SelectTrigger id="orientation">
                      <SelectValue placeholder="Select orientation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portrait">Portrait</SelectItem>
                      <SelectItem value="landscape">Landscape</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHeaderFooterDialogVisible(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header/Footer Editor Dialog */}
      {editingHeaderFooter && (
        <Dialog 
          open={editingHeaderFooter !== null} 
          onOpenChange={(open) => !open && setEditingHeaderFooter(null)}
        >
          <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              Edit {editingHeaderFooter === 'header' ? 'Header' : 'Footer'} Template
            </DialogTitle>
          </DialogHeader>
            
            <div className="space-y-4 py-4">
              <Textarea 
                className="h-[200px] font-mono"
                value={editingHeaderFooter === 'header' ? data.headerTemplate : data.footerTemplate}
                onChange={(e) => updateHeaderFooter(editingHeaderFooter, e.target.value)}
              />
              
              <div className="text-sm text-gray-500">
                <p>Available variables:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>[Document Title] - Title of the document</li>
                  <li>[Current Date] - Today's date</li>
                  <li>[Current Year] - Current year</li>
                  <li>[Page Number] - Current page number</li>
                  <li>[Total Pages] - Total number of pages</li>
                </ul>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingHeaderFooter(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <UpdateWithAIDialog
        visible={isUpdateWithAIDialogVisible && !data.documentCommand?.processing}
        onHide={() => setIsUpdateWithAIDialogVisible(false)}
        onUpdateWithAI={handleUpdateWithAI}
        contentMarkdown={contentMarkdown}
        documentStyle={documentStyle}
        context={context}
        linkedKnowledge={linkedKnowledge}
        onDuplicate={() => (window as any).__flowDuplicateNode?.(id)}
      />

      <NodeDataSettingsDialog
        visible={isDataSettingsDialogVisible && !data.documentCommand?.processing}
        onHide={() => setIsDataSettingsDialogVisible(false)}
        settings={dataSettings}
        nodeId={id}
        onSettingsChange={handleDataSettingsChange}
        onRemoveInputLink={handleRemoveInputLink}
        onSyncInputLink={handleSyncInputLink}
        forceShowAvailableNodes={true}
        debug={showDebugInfo}
      />

      <NodeConfigurationDialog
        visible={isConfigDialogVisible && !data.documentCommand?.processing}
        onHide={() => setIsConfigDialogVisible(false)}
        configuration={nodeConfig}
        onSave={(config) => { 
          setNodeConfig(config as CompositeNodeConfiguration)
          syncNodeData({ configuration: config as CompositeNodeConfiguration })
        }}
        additionalContent={compositeConfigContent}
      />

      <Steps
        enabled={introEnabled && !data.documentCommand?.processing}
        steps={introSteps}
        initialStep={0}
        onExit={() => setIntroEnabled(false)}
        options={introOptions}
      />

      {/* Reference Selector Dialog */}
      <NodeReferenceSelectorDialog
        visible={isReferenceDialogVisible}
        onHide={() => setIsReferenceDialogVisible(false)}
        availableNodes={availableNodes}
        onSave={handleSaveReferences}
        initialSelected={data.setReferences}
      />
    </>
  )
}

export default memo(NodeComposite)
